import React from 'react';
import { getMoneyBreakdown } from '../data/mock';

// Realistic Euro Bill component
const EuroBill = ({ bill, index }) => (
  <div
    className="relative transition-all duration-300 hover:scale-110 hover:z-20 cursor-pointer"
    style={{ 
      marginLeft: index > 0 ? '-16px' : '0',
      zIndex: 10 - index
    }}
  >
    <div 
      className="relative rounded shadow-lg overflow-hidden"
      style={{ 
        width: '56px', 
        height: '28px',
        background: `linear-gradient(135deg, ${bill.color} 0%, ${bill.color}dd 50%, ${bill.color}bb 100%)`,
        border: '1px solid #000000',
      }}
    >
      {/* Bill design elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1 left-1 w-3 h-3 border border-white/40 rounded-full" />
        <div className="absolute bottom-1 right-1 w-2 h-2 border border-white/40 rounded-full" />
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 4px, rgba(255,255,255,0.1) 4px, rgba(255,255,255,0.1) 5px)'
        }} />
      </div>
      {/* Value */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-white font-bold text-sm drop-shadow-md" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
          €{bill.label}
        </span>
      </div>
      {/* Shine effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent" />
    </div>
  </div>
);

// Realistic Euro Coin component
const EuroCoin = ({ coin, index }) => {
  const getSize = () => {
    if (coin.size === 'large') return { w: 32, h: 32, font: '10px' };
    if (coin.size === 'medium') return { w: 28, h: 28, font: '9px' };
    return { w: 24, h: 24, font: '8px' };
  };
  
  const size = getSize();
  
  const getColors = () => {
    if (coin.bicolor) return { outer: '#C0C0C0', inner: '#D4AF37' };
    if (coin.gold) return { outer: '#D4AF37', inner: '#D4AF37' };
    return { outer: '#B87333', inner: '#B87333' }; // copper
  };
  
  const colors = getColors();
  
  return (
    <div
      className="relative transition-all duration-300 hover:scale-110 hover:z-20 cursor-pointer"
      style={{ 
        marginLeft: index > 0 ? '4px' : '0',
        zIndex: 10 - index
      }}
    >
      <div 
        className="rounded-full shadow-lg flex items-center justify-center"
        style={{ 
          width: `${size.w}px`, 
          height: `${size.h}px`,
          background: coin.bicolor 
            ? `radial-gradient(circle at center, ${colors.inner} 0%, ${colors.inner} 55%, ${colors.outer} 55%, ${colors.outer} 100%)`
            : `radial-gradient(circle at 30% 30%, ${colors.outer}ee, ${colors.outer})`,
          border: '2px solid #000000',
          boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.3), inset 0 -1px 2px rgba(0,0,0,0.2), 0 2px 4px rgba(0,0,0,0.2)'
        }}
      >
        <span 
          className="font-bold"
          style={{ 
            fontSize: size.font, 
            color: coin.copper ? '#FFF' : '#1a1a1a',
            textShadow: coin.copper ? '0 1px 1px rgba(0,0,0,0.5)' : '0 1px 0 rgba(255,255,255,0.3)'
          }}
        >
          {coin.label}
        </span>
      </div>
    </div>
  );
};

const MoneyVisualization = ({ amount }) => {
  const { bills, coins } = getMoneyBreakdown(amount);
  
  if (bills.length === 0 && coins.length === 0) return null;
  
  // Limit display (fewer items for better mobile fit)
  const displayBills = bills.slice(0, 5);
  const displayCoins = coins.slice(0, 4);
  const hasMoreBills = bills.length > 5;
  const hasMoreCoins = coins.length > 4;
  
  return (
    <div className="flex flex-wrap items-center justify-center gap-2 py-3 px-2 overflow-hidden max-w-full">
      {/* Bills */}
      {displayBills.length > 0 && (
        <div className="flex items-center flex-shrink-0">
          {displayBills.map((bill, i) => (
            <EuroBill key={`bill-${i}`} bill={bill} index={i} />
          ))}
          {hasMoreBills && (
            <span className="text-xs text-slate-400 ml-1 font-medium">+{bills.length - 5}</span>
          )}
        </div>
      )}
      
      {/* Coins */}
      {displayCoins.length > 0 && (
        <div className="flex items-center flex-shrink-0">
          {displayCoins.map((coin, i) => (
            <EuroCoin key={`coin-${i}`} coin={coin} index={i} />
          ))}
          {hasMoreCoins && (
            <span className="text-xs text-slate-400 ml-1 font-medium">+{coins.length - 4}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default MoneyVisualization;
